import React, { useState, useEffect } from "react";
import { message, Spin } from "antd";
import { ApartmentOutlined } from "@ant-design/icons";
import api, { extractFireDACData } from "../services/api";

const ESTADOS = [
  "AC","AL","AP","AM","BA","CE","DF","ES","GO","MA","MT","MS","MG",
  "PA","PB","PR","PE","PI","RJ","RN","RS","RO","RR","SC","SP","SE","TO",
];

const DARK_NAV = "#1e3a4a";

// ── Helper FireDAC robusto (desembrulha .Original do formato FDBS) ─────
//    Mesmo padrão usado para ler as vendas no Dashboard.
const extractRows = (data) => {
  if (!data) return [];
  if (data.FDBS) {
    try {
      const mgr    = data.FDBS.Manager ?? data.FDBS.manager;
      const tables = mgr?.TableList ?? mgr?.tablelist ?? [];
      if (tables.length > 0) {
        const rowList = tables[0].RowList ?? tables[0].rowlist ?? [];
        if (rowList.length > 0)
          return rowList.map((r) => r.Original ?? r.Current ?? r);
      }
    } catch (e) {}
  }
  if (data.Table && Array.isArray(data.Table)) return data.Table;
  if (Array.isArray(data)) return data;
  // fallback: tenta o extrator padrão do projeto
  try { return extractFireDACData(data) || []; } catch (e) { return []; }
};

// Passa todas as chaves para minúsculo (FireDAC pode devolver MAIÚSCULO)
const normalize = (row) => {
  if (!row || typeof row !== "object") return {};
  const out = {};
  for (const [k, v] of Object.entries(row)) out[k.toLowerCase()] = v;
  return out;
};

// ── Helpers de máscara ────────────────────────────────────────────────
const maskCpf = (v = "") => {
  const d = v.replace(/\D/g, "").slice(0, 11);
  return d
    .replace(/(\d{3})(\d)/, "$1.$2")
    .replace(/(\d{3})(\d)/, "$1.$2")
    .replace(/(\d{3})(\d{1,2})$/, "$1-$2");
};

const maskCnpj = (v = "") => {
  const d = v.replace(/\D/g, "").slice(0, 14);
  return d
    .replace(/(\d{2})(\d)/, "$1.$2")
    .replace(/(\d{3})(\d)/, "$1.$2")
    .replace(/(\d{3})(\d)/, "$1/$2")
    .replace(/(\d{4})(\d{1,2})$/, "$1-$2");
};

const maskCelular = (v = "") => {
  const d = v.replace(/\D/g, "").slice(0, 11);
  if (!d) return "";
  if (d.length <= 10)
    return d.replace(/(\d{2})(\d{4})(\d{0,4})/, "($1) $2-$3");
  return d.replace(/(\d{2})(\d{5})(\d{0,4})/, "($1) $2-$3");
};

const maskCep = (v = "") => {
  const d = v.replace(/\D/g, "").slice(0, 8);
  if (!d) return "";
  return d.replace(/(\d{5})(\d{0,3})/, "$1-$2");
};

// ── Campo genérico ────────────────────────────────────────────────────
function Campo({ label, value, onChange, placeholder, type = "text", readOnly, suffix, span = 1 }) {
  return (
    <div style={{ gridColumn: `span ${span}` }}>
      <div style={{ fontSize: 11, color: "#888", marginBottom: 5, fontWeight: 500, letterSpacing: "0.3px" }}>
        {label}
      </div>
      <div style={{
        display: "flex", alignItems: "center",
        background: readOnly ? "#f7f8fa" : "#fff",
        border: "1px solid #dde1e9", borderRadius: 6,
        height: 38, paddingLeft: 12, paddingRight: suffix ? 10 : 12,
        transition: "border-color 0.15s",
      }}
        onFocus={() => {}}
      >
        <input
          type={type}
          readOnly={readOnly}
          value={value ?? ""}
          onChange={onChange ? (e) => onChange(e.target.value) : undefined}
          placeholder={placeholder || ""}
          style={{
            flex: 1, border: "none", background: "transparent",
            outline: "none", fontSize: 13, color: "#333",
            cursor: readOnly ? "default" : "text",
          }}
        />
        {suffix && <span style={{ color: "#bbb", fontSize: 14, marginLeft: 6 }}>{suffix}</span>}
      </div>
    </div>
  );
}

// ── Componente principal ──────────────────────────────────────────────
export default function DadosEmpresa({ clienteId: clienteIdProp }) {
  const [loading, setLoading]   = useState(false);
  const [saving, setSaving]     = useState(false);
  const [clienteId, setClienteId] = useState(null);
  const [buscandoCep, setBuscandoCep] = useState(false);

  const [form, setForm] = useState({
    tipo:         "Pessoa Física",
    cpfCnpj:      "",
    nome:         "",
    email:        "",
    celular:      "",
    cep:          "",
    logradouro:   "",
    numero:       "",
    complemento:  "",
    bairro:       "",
    cidade:       "",
    estado:       "",
  });

  // ── Resolve clienteId ────────────────────────────────────────────
  useEffect(() => {
    let id = clienteIdProp;
    try {
      const u = JSON.parse(localStorage.getItem("user") || "{}");
      if (!id) id = u.cli_id || u.id;
    } catch (e) {}
    if (!id) id = parseInt(localStorage.getItem("userID") || "0") || null;
    if (id) setClienteId(id);
    else message.error("Usuário não autenticado.");
  }, [clienteIdProp]);

  // ── Carrega dados existentes ─────────────────────────────────────
  useEffect(() => {
    if (!clienteId) return;
    setLoading(true);
    api.get("/ServerPrincipal/PesquisaEmpresa", { params: { Login: clienteId } })
      .then((r) => {
        // Desembrulha o formato FDBS (.Original) e normaliza as chaves
        const rows = extractRows(r.data).map(normalize);
        if (rows && rows.length > 0) {
          const d = rows[0];

          const tipo   = d.emp_tipo ?? d.tipo ?? "Pessoa Física";
          const rawDoc = String(d.emp_cpf_cnpj ?? d.cpf ?? "");
          const docFmt = rawDoc
            ? (tipo === "Pessoa Jurídica" ? maskCnpj(rawDoc) : maskCpf(rawDoc))
            : "";

          setForm({
            tipo,
            cpfCnpj:     d.emp_cpf_cnpj    ?? d.emp_cpf_cnpj  ?? "",
            nome:        d.emp_nome        ?? d.nome        ?? "",
            email:       d.emp_email       ?? d.email       ?? "",
            celular:     maskCelular(String(d.emp_celular ?? d.celular ?? "")),
            cep:         maskCep(String(d.emp_cep ?? d.cep ?? "")),
            logradouro:  d.emp_logradouro  ?? d.logradouro  ?? "",
            numero:      d.emp_numero       != null ? String(d.emp_numero ?? d.numero ?? "") : (d.numero ?? ""),
            complemento: d.emp_complemento ?? d.complemento ?? "",
            bairro:      d.emp_bairro      ?? d.bairro      ?? "",
            cidade:      d.emp_cidade      ?? d.cidade      ?? "",
            estado:      d.emp_estado      ?? d.estado      ?? "",
          });
        }
        // Se nao houver linhas, mantem o form em branco (cadastro novo).
      })
      .catch((e) => { console.error("[Empresa] erro ao buscar dados:", e); })
      .finally(() => setLoading(false));
  }, [clienteId]);

  // ── Busca CEP (ViaCEP) ───────────────────────────────────────────
  const buscarCep = async (cep) => {
    const raw = cep.replace(/\D/g, "");
    if (raw.length !== 8) return;
    setBuscandoCep(true);
    try {
      const res  = await fetch(`https://viacep.com.br/ws/${raw}/json/`);
      const data = await res.json();
      if (!data.erro) {
        setForm((prev) => ({
          ...prev,
          logradouro: data.logradouro || prev.logradouro,
          bairro:     data.bairro     || prev.bairro,
          cidade:     data.localidade || prev.cidade,
          estado:     data.uf         || prev.estado,
        }));
      }
    } catch (_) {}
    finally { setBuscandoCep(false); }
  };

  const set = (field) => (value) => setForm((prev) => ({ ...prev, [field]: value }));

  // ── Salvar ────────────────────────────────────────────────────────
  const handleSalvar = async () => {
    if (!form.nome || !form.email) {
      message.error("Nome e e-mail são obrigatórios.");
      return;
    }
    setSaving(true);
    try {
      await api.post("/ServerPrincipal/InserirEmpresa", {
        Tipo:        form.tipo,
        CpfCnpj:     form.cpfCnpj.replace(/\D/g, ""),
        Nome:        form.nome,
        Email:       form.email,
        Celular:     form.celular.replace(/\D/g, ""),
        Cep:         form.cep.replace(/\D/g, ""),
        Largadouro:  form.logradouro,
        Numero:      form.numero,
        Complemento: form.complemento,
        Bairro:      form.bairro,
        Cidade:      form.cidade,
        Estado:      form.estado,
        IDLogin:     clienteId,
      });
      message.success("Dados gravados com sucesso.");
    } catch (e) {
      message.error(e.response?.data || "Erro ao salvar dados.");
    } finally {
      setSaving(false);
    }
  };

  // ── Máscara CPF/CNPJ conforme tipo ───────────────────────────────
  const handleCpfCnpj = (v) => {
    const masked = form.tipo === "Pessoa Física" ? maskCpf(v) : maskCnpj(v);
    setForm((prev) => ({ ...prev, cpfCnpj: masked }));
  };

  const handleTipo = (v) => {
    setForm((prev) => ({ ...prev, tipo: v, cpfCnpj: "" }));
  };

  // ── Estilos base ─────────────────────────────────────────────────
  // OBS responsividade: gridTemplateColumns virou classe CSS (via o
  // <style> lá embaixo) em vez de inline, porque inline style não dá
  // pra sobrescrever com @media query. As colunas fixas em px (ex:
  // "160px 1fr 120px 160px" do bloco de endereço) eram o principal
  // culpado de quebrar em tela de celular -- 4 colunas fixas não têm
  // como caber em ~360px de largura.
  const sectionLabel = {
    fontSize: 13, fontWeight: 700, color: "#444",
    marginBottom: 14, marginTop: 24,
    display: "flex", alignItems: "center", gap: 6,
  };
  const selectStyle = {
    width: "100%", height: 38, borderRadius: 6,
    border: "1px solid #dde1e9", background: "#fff",
    fontSize: 13, color: "#333",
    padding: "0 10px", outline: "none", cursor: "pointer",
  };

  return (
    <div className="demp-container" style={{ minHeight: "100vh", background: "#f0f2f5", padding: "24px" }}>
      <Spin spinning={loading || buscandoCep}>
        <div className="demp-card" style={{
          maxWidth: 780,
          background: "#fff",
          borderRadius: 10,
          boxShadow: "0 1px 6px rgba(0,0,0,0.08)",
          overflow: "hidden",
        }}>

          {/* Header */}
          <div className="demp-header" style={{
            display: "flex", alignItems: "center", gap: 8,
            padding: "16px 24px",
            borderBottom: "1px solid #f0f0f0",
          }}>
            <ApartmentOutlined style={{ fontSize: 16, color: "#555" }} />
            <span style={{ fontSize: 15, fontWeight: 700, color: "#222" }}>Dados da Empresa</span>
          </div>

          <div className="demp-body" style={{ padding: "4px 24px 28px" }}>

            {/* Tipo + CPF/CNPJ */}
            <div className="demp-grid demp-grid-2">
              <div>
                <div style={{ fontSize: 11, color: "#888", marginBottom: 5, fontWeight: 500 }}>
                  Tipo do Negócio:
                </div>
                <select
                  value={form.tipo}
                  onChange={(e) => handleTipo(e.target.value)}
                  style={selectStyle}
                >
                  <option value="Pessoa Física">Pessoa Física</option>
                  <option value="Pessoa Jurídica">Pessoa Jurídica</option>
                </select>
              </div>

              <Campo
                label={form.tipo === "Pessoa Física" ? "CPF" : "CNPJ"}
                value={form.cpfCnpj}
                onChange={handleCpfCnpj}
                placeholder={form.tipo === "Pessoa Física" ? "000.000.000-00" : "00.000.000/0000-00"}
                suffix="#"
              />
            </div>

            {/* Nome + Email + Celular */}
            <div className="demp-grid demp-grid-3" style={{ marginTop: 14 }}>
              <Campo
                label="Nome da Empresa"
                value={form.nome}
                onChange={set("nome")}
                placeholder="Nome completo ou razão social"
                span={1}
              />
              <Campo
                label="Email de contato"
                value={form.email}
                onChange={set("email")}
                placeholder="email@exemplo.com"
                suffix="✉"
              />
              <Campo
                label="Celular de contato"
                value={form.celular}
                onChange={(v) => set("celular")(maskCelular(v))}
                placeholder="(xx) xxxxx-xxxx"
                suffix="📞"
              />
            </div>

            {/* Seção Endereço */}
            <div style={{ ...sectionLabel, marginTop: 28 }}>
              ENDEREÇO
            </div>
            <div style={{ height: 1, background: "#f0f0f0", marginBottom: 16, marginTop: -8 }} />

            {/* CEP + Logradouro + Número + Complemento */}
            <div className="demp-grid demp-grid-endereco">
              <div>
                <div style={{ fontSize: 11, color: "#888", marginBottom: 5, fontWeight: 500 }}>CEP</div>
                <div style={{
                  display: "flex", alignItems: "center",
                  background: "#fff", border: "1px solid #dde1e9",
                  borderRadius: 6, height: 38, paddingLeft: 12, paddingRight: 12,
                }}>
                  <input
                    value={form.cep}
                    onChange={(e) => {
                      const masked = maskCep(e.target.value);
                      set("cep")(masked);
                      if (masked.replace(/\D/g, "").length === 8) buscarCep(masked);
                    }}
                    placeholder="00000-000"
                    style={{ flex: 1, border: "none", background: "transparent", outline: "none", fontSize: 13, color: "#333" }}
                  />
                </div>
              </div>
              <Campo label="Logradouro"   value={form.logradouro}  onChange={set("logradouro")}  placeholder="Rua, Avenida..." />
              <Campo label="Número"       value={form.numero}      onChange={set("numero")}      placeholder="Nº" />
              <Campo label="Complemento"  value={form.complemento} onChange={set("complemento")} placeholder="Apto, Sala..." />
            </div>

            {/* Bairro + Cidade + Estado */}
            <div className="demp-grid demp-grid-bairro" style={{ marginTop: 14 }}>
              <Campo label="Bairro" value={form.bairro} onChange={set("bairro")} placeholder="Bairro" />
              <Campo label="Cidade" value={form.cidade} onChange={set("cidade")} placeholder="Cidade" />
              <div>
                <div style={{ fontSize: 11, color: "#888", marginBottom: 5, fontWeight: 500 }}>Estado</div>
                <select
                  value={form.estado}
                  onChange={(e) => set("estado")(e.target.value)}
                  style={selectStyle}
                >
                  <option value="">UF</option>
                  {ESTADOS.map((uf) => (
                    <option key={uf} value={uf}>{uf}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Botão Salvar */}
            <div className="demp-save-row" style={{ display: "flex", justifyContent: "flex-end", marginTop: 28 }}>
              <button
                className="demp-save-btn"
                onClick={handleSalvar}
                disabled={saving}
                style={{
                  padding: "9px 28px",
                  background: saving ? "#bfbfbf" : DARK_NAV,
                  color: "#fff",
                  border: "none",
                  borderRadius: 6,
                  fontSize: 13,
                  fontWeight: 700,
                  letterSpacing: "0.06em",
                  cursor: saving ? "not-allowed" : "pointer",
                  transition: "background 0.2s",
                }}
              >
                {saving ? "SALVANDO..." : "SALVAR"}
              </button>
            </div>
          </div>
        </div>
      </Spin>

      <style>{`
        .demp-grid {
          display: grid;
          gap: 14px 16px;
          margin-bottom: 4px;
        }
        .demp-grid-2       { grid-template-columns: 1fr 1fr; }
        .demp-grid-3       { grid-template-columns: 1fr 1fr 1fr; }
        .demp-grid-endereco{ grid-template-columns: 160px 1fr 120px 160px; }
        .demp-grid-bairro  { grid-template-columns: 1fr 1fr 120px; }

        /* Tablet: 4 e 3 colunas apertam demais, cai pra 2 */
        @media (max-width: 860px) {
          .demp-grid-3        { grid-template-columns: 1fr 1fr; }
          .demp-grid-endereco { grid-template-columns: 1fr 1fr; }
          .demp-grid-bairro   { grid-template-columns: 1fr 1fr; }
        }

        /* Celular: tudo empilha numa coluna só, senão os campos ficam
           estreitos demais pra digitar/tocar confortavelmente */
        @media (max-width: 600px) {
          .demp-container { padding: 10px !important; }
          .demp-card       { border-radius: 8px; }
          .demp-header     { padding: 14px 16px !important; }
          .demp-body       { padding: 4px 16px 20px !important; }
          .demp-grid-2, .demp-grid-3, .demp-grid-endereco, .demp-grid-bairro {
            grid-template-columns: 1fr !important;
          }
          .demp-save-row  { justify-content: stretch !important; }
          .demp-save-btn  { width: 100%; padding: 12px 0 !important; }
        }
      `}</style>
    </div>
  );
}